import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import {
  ButtonComponent,
  ModalComponent,
  SpinnerComponent,
} from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { PlatformDocument } from '../../models/platform.models';
import { PlatformProfileService } from '../../services/platform-profile.service';

@Component({
  selector: 'app-platform-document-viewer-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    ModalComponent,
    SpinnerComponent,
    TranslocoPipe,
    NgxExtendedPdfViewerModule,
  ],
  template: `
    <ct-lib-modal
      #modal
      [medium]="true"
      [label]="document()?.name ?? ('jobboard.platform.profile.sections.documents.preview-title' | transloco)"
      [footer]="footer"
      (onCloseEnd)="onCloseEnd()"
    >
      <div class="viewer">
        @if (loading()) {
          <div class="state">
            <ct-lib-spinner></ct-lib-spinner>
            <p>{{ 'jobboard.platform.profile.sections.documents.preview-loading' | transloco }}</p>
          </div>
        } @else if (error()) {
          <div class="state">
            <p>{{ 'jobboard.platform.profile.sections.documents.preview-error' | transloco }}</p>
            @if (src()) {
              <app-button variant="primary" (click)="download()">
                {{ 'jobboard.platform.profile.sections.documents.preview-download' | transloco }}
              </app-button>
            }
          </div>
        } @else if (isImage()) {
          <div class="image-viewer">
            <img [src]="src()!" [alt]="document()?.name ?? ''" />
          </div>
        } @else if (src()) {
          <ngx-extended-pdf-viewer
            [src]="src()!"
            [zoom]="'page-width'"
            height="100%"
            [showToolbar]="false"
            (pdfLoadingFailed)="onPdfFailed()"
          />
        }
      </div>
    </ct-lib-modal>

    <ng-template #footer>
      <div class="footer-actions">
        @if (src()) {
          <app-button variant="primary" (click)="download()">
            {{ 'jobboard.platform.profile.sections.documents.preview-download' | transloco }}
          </app-button>
        }
        <app-button variant="white-grey" (click)="close()">
          {{ 'jobboard.platform.profile.sections.documents.preview-close' | transloco }}
        </app-button>
      </div>
    </ng-template>
  `,
  styles: `
    :host {
      display: contents;
    }

    .viewer {
      min-height: 70vh;
      height: 70vh;
      display: flex;
      flex-direction: column;
    }

    .state {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
      text-align: center;
    }

    .image-viewer {
      flex: 1;
      overflow: auto;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding: 1rem;
    }

    .image-viewer img {
      max-width: 100%;
      height: auto;
    }

    .footer-actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 0.75rem;
      width: 100%;
    }
  `,
})
export class PlatformDocumentViewerModalComponent {
  private readonly profile = inject(PlatformProfileService);
  private readonly toasts = inject(ToastsService);

  private readonly modal = viewChild.required<ModalComponent>('modal');

  readonly document = signal<Pick<PlatformDocument, 'id' | 'name'> | null>(null);
  readonly src = signal<string | null>(null);
  readonly loading = signal(false);
  readonly error = signal(false);

  readonly isImage = computed(() => {
    const name = this.document()?.name?.toLowerCase() ?? '';
    return /\.(png|jpe?g|gif|webp|svg)$/.test(name);
  });

  /**
   * Opens the preview modal for a profile or importable document.
   * @param document - Document id and file name to preview
   */
  open(document: Pick<PlatformDocument, 'id' | 'name'>): void {
    this.document.set(document);
    this.src.set(null);
    this.error.set(false);
    this.loading.set(true);
    this.modal().open();
    this.profile.documentUrl(document.id).subscribe({
      next: (url) => {
        this.src.set(url);
        this.loading.set(false);
        if (!this.isPdf(document.name) && !this.isImage()) {
          this.error.set(true);
        }
      },
      error: () => {
        this.loading.set(false);
        this.error.set(true);
        this.toasts.warning(
          'jobboard.platform.profile.sections.documents.preview-error'
        );
      },
    });
  }

  /**
   * Opens the current document URL in a new tab for download.
   */
  download(): void {
    const url = this.src();
    if (!url) {
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Closes the preview modal.
   */
  close(): void {
    this.modal().close();
  }

  /**
   * Resets preview state after the modal animation ends.
   */
  onCloseEnd(): void {
    this.document.set(null);
    this.src.set(null);
    this.loading.set(false);
    this.error.set(false);
  }

  /**
   * Marks the PDF as unsupported when loading fails.
   */
  onPdfFailed(): void {
    this.error.set(true);
  }

  /**
   * Whether a file name looks like a PDF.
   * @param name - File name
   * @returns True for PDF files
   */
  private isPdf(name: string): boolean {
    return name.toLowerCase().endsWith('.pdf');
  }
}
